import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ADMIN_TEST_INSTRUCTIONS_API =
    process.env.REACT_APP_ADMIN_TEST_INSTRUCTIONS_API || `${API_BASE_URL}/api/admin-app/test-instructions/`;

const AdminInstructionsPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [form, setForm] = useState({
        title: '',
        is_active: true,
    });

    const headers = useMemo(() => {
        const token = (localStorage.getItem('guest_access_token') || sessionStorage.getItem('guest_access_token') || '').trim();
        if (!token) return null;
        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    }, []);

    const detailUrl = (id) => `${ADMIN_TEST_INSTRUCTIONS_API}${id}/`;

    const resetForm = () => {
        setEditingId(null);
        setIsFormOpen(false);
        setForm({
            title: '',
            is_active: true,
        });
    };

    const fetchRows = async () => {
        if (!headers) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(ADMIN_TEST_INSTRUCTIONS_API, { headers });
            const data = response.ok ? await response.json() : [];
            setRows(Array.isArray(data) ? data : []);
        } catch (error) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headers]);

    const openCreateForm = () => {
        resetForm();
        setErrorMessage('');
        setSuccessMessage('');
        setIsFormOpen(true);
    };

    const handleInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errorMessage) setErrorMessage('');
        if (successMessage) setSuccessMessage('');
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setForm({
            title: item.title || '',
            is_active: Boolean(item.is_active),
        });
        setErrorMessage('');
        setSuccessMessage('Editing selected instruction.');
        setIsFormOpen(true);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        if (!headers) {
            setErrorMessage('Session expired. Please log in again.');
            return;
        }

        const title = form.title.trim();
        if (!title) {
            setErrorMessage('Instruction title is required.');
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(editingId ? detailUrl(editingId) : ADMIN_TEST_INSTRUCTIONS_API, {
                method: editingId ? 'PATCH' : 'POST',
                headers,
                body: JSON.stringify({
                        title,
                        is_active: Boolean(form.is_active),
                    }),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                setErrorMessage(data?.error || data?.title?.[0] || 'Unable to save instruction.');
                return;
            }

            setSuccessMessage(editingId ? 'Instruction updated successfully.' : 'Instruction added successfully.');
            resetForm();
            await fetchRows();
        } catch (error) {
            setErrorMessage('Unable to reach server right now.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (itemId) => {
        if (!headers) {
            setErrorMessage('Session expired. Please log in again.');
            return;
        }

        const result = await Swal.fire({
            title: 'Delete instruction?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;

        setSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const response = await fetch(detailUrl(itemId), {
                method: 'DELETE',
                headers,
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                setErrorMessage(data?.error || 'Unable to delete instruction.');
                return;
            }

            if (editingId === itemId) {
                resetForm();
            }

            setSuccessMessage('Instruction deleted successfully.');
            await fetchRows();
        } catch (error) {
            setErrorMessage('Unable to reach server right now.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-panel-card admin-table-section">
            {isFormOpen ? (
                <div className="admin-top-sheet-backdrop" onClick={resetForm}>
                    <form className="admin-college-form admin-top-sheet" onSubmit={handleSubmit} onClick={(event) => event.stopPropagation()}>
                        <div className="admin-top-sheet-header">
                            <h4 className="admin-college-dropdown-title">{editingId ? 'Edit Instruction' : 'Add Instruction'}</h4>
                            <button
                                type="button"
                                className="admin-top-sheet-close"
                                onClick={resetForm}
                                disabled={submitting}
                                aria-label="Close instruction form"
                            >
                                x
                            </button>
                        </div>

                        <div className="admin-college-form-grid">
                            <div>
                                <label className="admin-security-label" htmlFor="instruction-title">Instruction</label>
                                <input
                                    id="instruction-title"
                                    name="title"
                                    type="text"
                                    className="admin-security-input"
                                    value={form.title}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                />
                            </div>
                            <div className="admin-instruction-form-row">
                                <label className="admin-instruction-toggle">
                                    <input
                                        name="is_active"
                                        type="checkbox"
                                        checked={form.is_active}
                                        onChange={handleInputChange}
                                        disabled={submitting}
                                    />
                                    <span>Active</span>
                                </label>
                            </div>
                        </div>

                        <div className="admin-college-actions">
                            <button type="submit" className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn" disabled={submitting}>
                                {submitting ? 'Saving...' : editingId ? 'Update Instruction' : 'Add Instruction'}
                            </button>
                            <button type="button" className="admin-profile-action-btn" onClick={resetForm} disabled={submitting}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}

            <div className="admin-college-header">
                <div>
                    <h3>Test Instructions</h3>
                    <p className="admin-subtitle">Create and order the instructions shown to students before a test starts.</p>
                </div>
                <button
                    type="button"
                    className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn"
                    onClick={openCreateForm}
                    disabled={submitting}
                >
                    Add Instruction
                </button>
            </div>

            {errorMessage ? <div className="admin-security-feedback admin-security-feedback-error">{errorMessage}</div> : null}
            {successMessage ? <div className="admin-security-feedback admin-security-feedback-success">{successMessage}</div> : null}

            <div className="admin-table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Instruction</th>
                            <th>Status</th>
                            <th>Updated</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="4">Loading instructions...</td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan="4">No instruction records found.</td>
                            </tr>
                        ) : (
                            rows.map((item) => (
                                <tr key={item.id}>
                                    <td className="admin-instruction-cell">{item.title || '-'}</td>
                                    <td>
                                        <span className={`admin-status-pill${item.is_active ? ' is-active' : ' is-muted'}`}>
                                            {item.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>{item.updated_at ? new Date(item.updated_at).toLocaleString() : '-'}</td>
                                    <td>
                                        <div className="admin-inline-actions">
                                            <button
                                                type="button"
                                                className="admin-profile-action-btn"
                                                onClick={() => handleEdit(item)}
                                                disabled={submitting}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="admin-profile-action-btn admin-profile-action-btn-danger"
                                                onClick={() => handleDelete(item.id)}
                                                disabled={submitting}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminInstructionsPage;
